import {
  Absolute,
  Carousel,
  Column,
  Header,
  ImageFromCdn,
  Row,
  Typography,
} from '@/components'
import { ThemeColor } from '@/enums'
import { GoogleRatingIcon, ShieldCheck, SureinTrustLogo } from '@/icons'
import { GoogleTrustStars } from '@/icons/GoogleTrustStars'
import { TrustPilotExtended } from '@/icons/TrustPilotExtended'
import { useContactModal } from '@/providers'
import { paths } from '@/routes/paths'
import { useGoToPath } from '@/utils'
import { Button } from '@surein/ui'
import { Check } from 'lucide-react'
import { range } from 'ramda'
import { Trans, useTranslation } from 'react-i18next'
import { useOutletContext } from 'react-router-dom'
import {
  CheckIconBackground,
  ImageContainer,
  StyledSSL,
  SuccessPageContainer,
  SuccessPageHeader,
} from './styles'
import { UserProfile } from '@/types/user-profile'

interface Messages {
  title: string
  anExpertWillContact: string
  contact: string
  backToDashboard: string
  owner: string
  managingDirector: string
  leadSuccessTestimonials: string[]
}

const useMessages = (): Messages => {
  const { t } = useTranslation()

  return {
    title: t('leadSuccessTitle'),
    anExpertWillContact: t('anExpertWillContact'),
    contact: t('common.contactUs'),
    owner: t('owner'),
    managingDirector: t('managingDirector'),
    backToDashboard: t('back_to_dashboard'),
    leadSuccessTestimonials: t('leadSuccessTestimonials', {
      returnObjects: true,
    }) as string[],
  }
}

interface OutletContext {
  user: UserProfile
}

interface Testimonial {
  name: string
  role: string
  img: string
}

const testimonials: Testimonial[] = [
  {
    name: 'Jutta Tischendorf',
    role: '{{owner}}: Cafe Tischendorf',
    img: 'testimonial1',
  },
  {
    name: 'Constantin von Bergmann-Korn',
    role: 'Partnership Manager Adyen',
    img: 'testimonial2',
  },
  {
    name: 'Cem Yilmaz',
    role: '{{md}}: Glüxgefühl',
    img: 'testimonial3',
  },
  {
    name: 'Felix Leisegang',
    role: '{{owner}}: Meatballery',
    img: 'testimonial4',
  },
]

const TrustPartners: React.FC = () => (
  <Row alignItems="center" gap="18px">
    <StyledSSL alignItems="center" gap="4px">
      <ShieldCheck color={ThemeColor.success4} />
      <Typography singleLine variant="badgeText">
        <Trans i18nKey="ssl" />
      </Typography>
    </StyledSSL>
    <TrustPilotExtended width="170px" />
    <SureinTrustLogo height="28px" width="32px" />
    <GoogleTrustStars height="30px" width="120px" />
  </Row>
)

interface SuccessPageProps {
  onLogoClick?: () => void
}

export const SuccessPage: React.FC<SuccessPageProps> = ({ onLogoClick }) => {
  const messages = useMessages()
  const { openContact } = useContactModal()
  const goToDashboard = useGoToPath(paths.dashboard)
  const { user } = useOutletContext<OutletContext>()

  return (
    <Absolute left={0} right={0} top={0}>
      <Header onLogoClick={onLogoClick} simple />
      <SuccessPageContainer>
        <SuccessPageHeader>
          <Column gap="32px">
            <Column gap="10px">
              <CheckIconBackground>
                <Check />
              </CheckIconBackground>
              <Typography bold variant="h2">
                {messages.title}
              </Typography>
            </Column>
            <Typography color={ThemeColor.b50} variant="p1Body">
              {messages.anExpertWillContact}
            </Typography>
            <Row gap="16px">
              {Boolean(user) && (
                <Button
                  onClick={() => goToDashboard()}
                  variant="primary"
                  shape="square"
                >
                  {messages.backToDashboard}
                </Button>
              )}
              <Button
                onClick={openContact}
                variant={user ? 'secondary' : 'primary'}
                shape="square"
              >
                {messages.contact}
              </Button>
            </Row>
            <TrustPartners />
          </Column>
          <ImageContainer>
            <ImageFromCdn fallbackType="jpg" name="the-team" type="webp" />
          </ImageContainer>
        </SuccessPageHeader>
        <Column alignItems="center" gap="72px">
          <Carousel autoPlayDuration={5000} width={680}>
            {testimonials.map(({ name, role, img }, i) => (
              <Column gap="12px" key={`${name}-${i}`}>
                <Row gap="16px">
                  <ImageFromCdn
                    fallbackType="jpg"
                    name={img}
                    type="webp"
                    width={56}
                  />
                  <Column gap="4px">
                    <Typography color={ThemeColor.b50} variant="p2Body">
                      {name}
                    </Typography>
                    <Typography color={ThemeColor.b50} variant="badgeText">
                      {role
                        .replace('{{owner}}', messages.owner)
                        .replace('{{md}}', messages.managingDirector)}
                    </Typography>
                    <Row>
                      {range(1, 6).map((star) => (
                        <GoogleRatingIcon
                          key={star}
                          color={ThemeColor.warning1}
                          size="menu"
                        />
                      ))}
                    </Row>
                  </Column>
                </Row>
                <Typography color={ThemeColor.b50} variant="p1Body">
                  {messages.leadSuccessTestimonials[i]}
                </Typography>
              </Column>
            ))}
          </Carousel>
          <div />
        </Column>
      </SuccessPageContainer>
    </Absolute>
  )
}
